import { Box, Button, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/HighlightOff';
import { useNavigate } from 'react-router-dom';
import { CenteredAuthCard } from './CenteredAuthCard';

interface StateErrorProps {
  /** Called when the user clicks "Try again". Defaults to navigating to /login. */
  onRetry?: () => void;
}

/**
 * SSO callback error state.
 * Copy is deliberately non-enumerating — never confirms or denies account existence.
 */
export function StateError({ onRetry }: StateErrorProps) {
  const navigate = useNavigate();
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      navigate('/login', { replace: true });
    }
  };

  return (
    <CenteredAuthCard>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: 'statusSoft.danger',
          color: 'error.main',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 0.5,
        }}
      >
        <ErrorIcon sx={{ fontSize: 28 }} />
      </Box>

      <Typography
        component="h1"
        sx={{ fontSize: '1.375rem', fontWeight: 500, letterSpacing: '-0.025em', textAlign: 'center' }}
      >
        Can&apos;t sign in
      </Typography>

      <Typography sx={{ fontSize: '0.84375rem', color: 'text.secondary', textAlign: 'center', lineHeight: 1.5 }}>
        We couldn&apos;t complete your sign-in. Please check your details or contact your workspace
        administrator.
      </Typography>

      <Box
        sx={{
          p: '12px 14px',
          bgcolor: 'surface.faint',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          fontSize: '0.75rem',
          color: 'text.secondary',
          lineHeight: 1.55,
        }}
      >
        <Typography sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: 'inherit' }}>
          What you can try
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: '18px', color: 'text.disabled', fontSize: 'inherit' }}>
          <li>Check you&apos;re using your work account</li>
          <li>Ask your administrator if access has been set up</li>
          <li>Try signing in again from the start</li>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleRetry}
          sx={{ py: '11px', fontSize: '0.84375rem', fontWeight: 500 }}
        >
          Try again
        </Button>
        {/* Stub: navigates to /login — admin contact is deferred until workspace admin email is available (see refactor-backlog §F19). */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleRetry}
          sx={{ py: '11px', fontSize: '0.84375rem', fontWeight: 500 }}
        >
          Contact admin
        </Button>
      </Box>
    </CenteredAuthCard>
  );
}
